import type { UploadDetected } from '@tabservice/tabfile-client';
import { Card, SectionHeader } from '@/components/ui/card';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { formatCount } from '@/lib/format';

export type BoxTotal = NonNullable<UploadDetected['boxTotals']>[number];

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** A money total as $1,234.50; a count as a plain integer, the way the portal shows them. */
export function formatBoxTotal(box: Pick<BoxTotal, 'kind' | 'total'>): string {
  return box.kind === 'count' ? formatCount(box.total) : currency.format(box.total);
}

/**
 * What the file adds up to, box by box.
 *
 * The same table the Tab Service portal shows once a file's columns are mapped and checked.
 * A customer knows the total they expect for box 1; a column matched to the wrong box, or a
 * file exported with the wrong rows, shows here as a total that is off, before it is filed.
 * Boxes with values are rows; the empty ones are named in a line, so a box that should have money in it and has none is
 * as visible as one that is wrong.
 */
export function BoxTotals({
  boxes,
  recordCount,
  description,
  bare = false,
  unmappedColumns = [],
}: {
  boxes: BoxTotal[] | undefined;
  recordCount: number;
  description?: string;
  /** Just the table, for a place that already has its own heading. */
  bare?: boolean;
  /**
   * The file's columns that matched nothing. An empty box that has one of these as a
   * lookalike is named with it, because "No values in Golden Parachute" beside an ignored
   * BOX14_GOLDEN_PARACHUTE is one fact read from two sides, and the customer reads it as a
   * contradiction (TS-8328).
   */
  unmappedColumns?: string[];
}) {
  if (!boxes || boxes.length === 0) return null;
  // Only the boxes the file put something in are rows: a 1099-K has twenty boxes and a
  // typical file fills four. The empty ones are still named underneath, in one line,
  // because a box the person expected money in and finds empty is the mapping being wrong.
  const filled = boxes.filter((box) => box.records > 0);
  const empty = boxes.filter((box) => box.records === 0);
  const table = (
    <div className="flex flex-col gap-3">
      {filled.length > 0 ? (
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>Box</TH>
                <TH align="right">Records with a value</TH>
                <TH align="right">Total</TH>
              </tr>
            </THead>
            <TBody>
              {filled.map((box) => (
                <TR key={box.field} data-testid={`box-total-${box.field}`}>
                  <TD>
                    <span className="font-medium text-gray-3">{box.label}</span>
                    <span className="block text-sm text-muted-foreground">{box.field}</span>
                  </TD>
                  <TD numeric>{formatCount(box.records)}</TD>
                  <TD numeric className="font-medium">
                    {formatBoxTotal(box)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      ) : (
        <p className="text-base text-destructive">
          No box has a value in any record. If the file carries amounts, its columns were not read as boxes;
          check the column mapping.
        </p>
      )}
      {empty.length > 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="box-totals-empty">
          <span className="font-medium text-gray-3">No values in:</span>{' '}
          {empty.map((box) => box.label).join(', ')}.
          {lookalikes(empty, unmappedColumns).map(({ box, column }) => (
            <span key={box.field} className="block">
              Your column <span className="font-mono text-xs text-gray-3">{column}</span> was not read as {box.label}.
              Rename it <span className="font-mono text-xs text-gray-3">{box.field}</span> to fill that box.
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
  if (bare) return table;
  return (
    <Card>
      <SectionHeader
        title="Box totals"
        description={
          description ??
          `What the ${formatCount(recordCount)} records add up to, box by box. Check them against your own totals before continuing.`
        }
      />
      {table}
    </Card>
  );
}

/** Ignored columns that carry an empty box's words after a different box number. */
function lookalikes(empty: BoxTotal[], unmapped: string[]): { box: BoxTotal; column: string }[] {
  const out: { box: BoxTotal; column: string }[] = [];
  for (const box of empty) {
    const tail = boxTail(box.field);
    if (!tail) continue;
    const column = unmapped.find((name) => boxTail(name) === tail);
    if (column) out.push({ box, column });
  }
  return out;
}

/**
 * The words of a box column with its number taken off, the way the server's column mapping
 * reads them (packages/iris-engine, intake/columnMapping.ts): BOX14_GOLDEN_PARACHUTE and
 * BOX3_GOLDEN_PARACHUTE are the same words under different numbers. Kept in step with that
 * function by hand; the web does not depend on the engine package.
 */
function boxTail(name: string): string | null {
  const match = /^BOX[ _-]?(\d+[A-Za-z]?)[ _-]+(.+)$/i.exec(name.trim());
  if (!match) return null;
  const words = match[2].replace(/[^A-Za-z0-9]+/g, '').toUpperCase().replace(/(INDICATOR|IND)$/, '').replace(/S$/, '');
  return words || null;
}
